<template>
  <bm-tooltip top>
    <template #activator="{ on }">
      <v-btn
        :class="classes"
        :style="btnStyle"
        depressed
        v-bind="$attrs"
        :loading="loading || showStateWhileHandlingClick"
        :text="buttonType.toLowerCase() === 'ghost'"
        :outlined="buttonType.toLowerCase() === 'secondary'"
        :data-qa="dataQa"
        exact
        v-on="tooltipVal ? on : undefined"
        @click="clickHandler"
      >
        <template #loader>
          <!-- Standard v-btn behaviour-->
          <v-progress-circular
            v-if="!success"
            :indeterminate="true"
            :size="23"
            :width="2"
          ></v-progress-circular>
          <v-icon v-if="success" color="#fff">check</v-icon>
        </template>
        <slot>{{ successMessage || labelVal }}</slot>
      </v-btn>
    </template>
    <span v-if="tooltipVal">{{ tooltipVal }}</span>
  </bm-tooltip>
</template>

<script lang="ts">
import BmTooltip from "@/components/controls/input/BmTooltip.vue";
import BmLabelled from "@/components/controls/input/mixins/BmLabelled";
import type { PropType } from "vue";
import { defineComponent } from "vue";
import appStore from "@/store/modules/appStore";
export default defineComponent({
  name: "BmBtn",
  components: { BmTooltip },
  mixins: [BmLabelled],
  inheritAttrs: false,
  props: {
    buttonType: {
      type: String,
      default: "primary",
    },
    showActionState: {
      type: Boolean,
      default: false,
    },
    btnClass: {
      type: String,
      default: undefined,
    },
    btnStyle: {
      type: Object as PropType<Object | string>,
      default: undefined,
    },
    successMessage: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showStateWhileHandlingClick: false as boolean,
      success: false as boolean,
    };
  },
  computed: {
    classes(): string[] {
      const classes = ["bm-btn"];

      if (this.btnClass) {
        classes.push(this.btnClass);
      }

      if (this.showActionState) {
        if (this.showStateWhileHandlingClick) {
          classes.push("bm-btn-loading");
        }

        if (this.success) {
          classes.push("bm-btn-success");
        }
      }

      if (this.buttonType.toLowerCase() === "primary") {
        classes.push("primaryButton", "white--text");
      } else if (this.buttonType.toLowerCase() === "primary-danger") {
        classes.push("primary-danger");
      } else if (this.buttonType.toLowerCase() === "primary-success") {
        classes.push("primary-success");
      } else if (this.buttonType.toLowerCase() === "primary-warn") {
        classes.push("primary-warn");
      } else if (this.buttonType.toLowerCase() === "ghost") {
        classes.push("ghost", "primary--text");
      } else if (this.buttonType.toLowerCase() === "secondary") {
        classes.push(
          ...(appStore.state.theme === "dark"
            ? ["secondarybtn-dark"]
            : ["secondarybtn", "primary--text"])
        );
      } else if (this.buttonType.toLowerCase() === "largebtn") {
        classes.push("largebtn", "primary--text");
      } else if (this.buttonType.toLowerCase() === "largebtn-dotted") {
        classes.push("largebtn-dotted", "primary--text");
      } else if (this.buttonType.toLowerCase() === "largebtn-primary-border") {
        classes.push("largebtn-primary-border", "primary--text");
      } else {
        throw new Error(
          "Unrecognized bm-btn type: " + this.buttonType.toLowerCase()
        );
      }

      if (this.successMessage) {
        classes.push("not-clickable");
        classes.push("bm-btn-success");
      }

      return classes;
    },
  },
  methods: {
    async clickHandler(event: MouseEvent) {
      /* This is fairly hacky because it stops event propagation, but it seems to be in use elsewhere..
       * Rather than bubbling the event through the browser, we call the handler directly and await it if it
       * returns a promise
       */
      if (!this.showActionState) {
        this.$emit("click", event);
      } else {
        this.showStateWhileHandlingClick = true;
        // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
        if (typeof this.$listeners.click == "function") {
          // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
          const result = this.$listeners.click(event);
          if (result instanceof Promise) {
            try {
              await result;
              this.success = true;
              setTimeout(() => {
                this.success = false;
                this.showStateWhileHandlingClick = false;
              }, 2000);
            } catch {
              this.showStateWhileHandlingClick = false;
            }
          } else {
            this.showStateWhileHandlingClick = false;
          }
        } else {
          this.showStateWhileHandlingClick = false;
          this.$emit("click", event);
        }
      }
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

.bm-btn
  font-family: $secondary-font-family
  text-transform: none !important // Allows the text to be camel cased

.primary-danger
  color: #fff !important
  background-color: $bm-color-error-medium !important

.primary-success
  color: #FFF !important
  background-color: $bm-color-success-medium !important

.primary-warn
  color: $bm-color-text01 !important
  background-color: $bm-color-warning-medium !important

.secondarybtn
  border: 2px solid !important
  background-color: $bm-color-background01 !important

.secondarybtn-dark
  border: 2px solid $bm-color-brand02 !important
  background-color: $bm-color-background01 !important

.secondarybtn-dark .v-btn__content
  color: $bm-color-text01

.largebtn
  border: 1px solid !important
  background-color: $bm-color-background01 !important
  height: 70px !important

.largebtn-dotted
  border: 1px dotted !important
  background-color: $bm-color-background01 !important
  height: 70px !important

.v-application .bm-btn-success
  background-color : $bm-color-success-medium !important
  transition: background-color 0.25s ease !important

.not-clickable
  pointer-events: none

.largebtn-primary-border
  border: 2px solid $bm-color-brand01 !important
  height: 60px !important

@media print
  .bm-btn
    display: none

:deep(.v-btn__content)
  width: 100%
</style>
